import confetti from "canvas-confetti";
import { supabase } from "@/integrations/supabase/client";

/**
 * Gets the rave party mode setting from user preferences
 */
const getRavePartyMode = async (): Promise<boolean> => {
	try {
		const { data: { user } } = await supabase.auth.getUser();
		if (!user) return false;

		const { data } = await supabase
			.from('profiles')
			.select('settings')
			.eq('id', user.id)
			.single();

		if (data?.settings && typeof data.settings === 'object') {
			const settings = data.settings as { ravePartyMode?: boolean };
			return settings.ravePartyMode || false;
		}
		return false;
	} catch (error) {
		return false;
	}
};

/**
 * Soft celebratory confetti animation for when a candidate gets hired
 * Professional but joyful celebration with vibrant colors
 */
const triggerSoftConfetti = () => {
	const duration = 1500; // 1.5 seconds for professional feel
	const animationEnd = Date.now() + duration;
	
	// Expanded color palette - more vibrant and celebratory
	const colors = ["#22c55e", "#16a34a", "#15803d", "#fbbf24", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4"];
	
	// Initial celebratory burst from center-top - more particles
	confetti({
		particleCount: 150,
		spread: 120,
		origin: { y: 0.6 },
		colors: colors,
		gravity: 0.6,
		scalar: 1.4,
		drift: 0.1,
		startVelocity: 50
	});
	
	// Two side bursts for nice coverage - more particles
	setTimeout(() => {
		confetti({
			particleCount: 120,
			spread: 100,
			origin: { x: 0.2, y: 0.7 },
			colors: colors,
			gravity: 0.6,
			scalar: 1.4,
			drift: 0.1,
			startVelocity: 45
		});
		
		confetti({
			particleCount: 120,
			spread: 100,
			origin: { x: 0.8, y: 0.7 },
			colors: colors,
			gravity: 0.6,
			scalar: 1.4,
			drift: -0.1,
			startVelocity: 45
		});
	}, 300);
	
	// Enhanced continuous sparkles - more particles
	const interval = setInterval(() => {
		const timeLeft = animationEnd - Date.now();
		
		if (timeLeft <= 0) {
			clearInterval(interval);
			return;
		}
		
		confetti({
			particleCount: 50,
			spread: 80,
			origin: { 
				x: Math.random() * 0.6 + 0.2, // Center area
				y: Math.random() * 0.3 + 0.5
			},
			colors: colors,
			gravity: 0.7,
			scalar: 1.2,
			drift: Math.random() * 0.2 - 0.1,
			startVelocity: 35
		});
	}, 400);
};

/**
 * 🚀🎆⚡ REAL LASER & FIREWORK CELEBRATION ⚡🎆🚀
 * ACTUAL LASER BEAMS AND FIREWORK EXPLOSIONS WITH DOM EFFECTS!
 * NOW WITH REAL VISUAL EFFECTS YOU CAN ACTUALLY SEE!
 */

// Create laser beam element
const createLaserBeam = (startX: number, startY: number, endX: number, endY: number, color: string, duration: number = 500) => {
  const laser = document.createElement('div');
  const length = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
  const angle = Math.atan2(endY - startY, endX - startX) * 180 / Math.PI;
  
  laser.style.cssText = `
    position: fixed;
    left: ${startX}px;
    top: ${startY}px;
    width: ${length}px;
    height: 4px;
    background: linear-gradient(90deg, ${color}, #ffffff, ${color});
    transform: rotate(${angle}deg);
    transform-origin: 0 50%;
    box-shadow: 0 0 20px ${color}, 0 0 40px ${color}, 0 0 60px ${color};
    z-index: 10000;
    pointer-events: none;
    animation: laserPulse ${duration}ms ease-out forwards;
  `;
  
  // Add laser pulse animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes laserPulse {
      0% { opacity: 0; transform: rotate(${angle}deg) scaleX(0); }
      10% { opacity: 1; transform: rotate(${angle}deg) scaleX(1); }
      90% { opacity: 1; transform: rotate(${angle}deg) scaleX(1); }
      100% { opacity: 0; transform: rotate(${angle}deg) scaleX(1); }
    }
  `;
  document.head.appendChild(style);
  
  document.body.appendChild(laser);
  setTimeout(() => {
    document.body.removeChild(laser);
    document.head.removeChild(style);
  }, duration);
};

// Create firework explosion element
const createFireworkExplosion = (x: number, y: number, color: string, size: number = 100) => {
  const firework = document.createElement('div');
  firework.style.cssText = `
    position: fixed;
    left: ${x - size/2}px;
    top: ${y - size/2}px;
    width: ${size}px;
    height: ${size}px;
    border-radius: 50%;
    background: radial-gradient(circle, ${color} 0%, transparent 70%);
    box-shadow: 0 0 ${size}px ${color}, 0 0 ${size*2}px ${color};
    z-index: 10000;
    pointer-events: none;
    animation: fireworkBoom 800ms ease-out forwards;
  `;
  
  // Add firework explosion animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fireworkBoom {
      0% { 
        transform: scale(0);
        opacity: 1;
      }
      50% { 
        transform: scale(1);
        opacity: 0.8;
      }
      100% { 
        transform: scale(2);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);
  
  document.body.appendChild(firework);
  setTimeout(() => {
    document.body.removeChild(firework);
    document.head.removeChild(style);
  }, 800);
};

const triggerRavePartyConfetti = () => {
  const duration = 4000; // Reduced to 4 seconds for better performance
  const animationEnd = Date.now() + duration;
  
  // 🎵 PLAY CELEBRATION SOUND! 🎵
  let celebrationAudio: HTMLAudioElement | null = null;
  try {
    celebrationAudio = new Audio('/eea08230-c881-4bf5-a39a-69f7152a89b9.mp3');
    celebrationAudio.volume = 0.7; // Set volume to 70%
    celebrationAudio.play().catch(error => {
      console.log('Audio playback failed (user interaction may be required):', error);
    });
  } catch (error) {
    console.log('Audio setup failed:', error);
  }
  
  // LASER COLORS - BRIGHT AND VISIBLE!
  const laserColors = ["#ff0000", "#00ff00", "#0000ff", "#ffff00", "#ff00ff", "#00ffff"];
  const fireworkColors = [
    "#ff0000", "#ffa500", "#ffff00", "#00ff00", "#00ffff", "#0000ff", 
    "#8a2be2", "#ff1493", "#ffd700", "#32cd32"
  ];
  
  // 🎆 MASSIVE INITIAL CONFETTI BURSTS! 🎆
  for (let explosion = 0; explosion < 6; explosion++) {
    setTimeout(() => {
      confetti({
        particleCount: 150,
        spread: 140,
        origin: { 
          x: 0.1 + explosion * 0.15, 
          y: 0.5 + Math.random() * 0.3
        },
        colors: fireworkColors,
        gravity: 0.6,
        scalar: 2,
        drift: 0,
        startVelocity: 70,
        shapes: ['circle', 'square'] // Classic shapes only
      });
    }, explosion * 200);
  }
  
  // ⚡ REAL LASER LIGHT SHOW! ⚡
  for (let laser = 0; laser < 12; laser++) {
    setTimeout(() => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Horizontal laser sweep across screen
      createLaserBeam(
        0, 
        100 + laser * 40, 
        windowWidth, 
        100 + laser * 40, 
        laserColors[laser % laserColors.length], 
        600
      );
      
      // Vertical laser beam from top to bottom
      createLaserBeam(
        100 + laser * (windowWidth / 12), 
        0, 
        100 + laser * (windowWidth / 12), 
        windowHeight, 
        laserColors[(laser + 2) % laserColors.length], 
        600
      );
      
      // Diagonal laser strikes
      if (laser < 6) {
        createLaserBeam(
          laser < 3 ? 0 : windowWidth, 
          200 + laser * 80, 
          laser < 3 ? windowWidth : 0, 
          200 + laser * 80 + 200, 
          laserColors[(laser + 4) % laserColors.length], 
          800
        );
      }
    }, laser * 150);
  }
  
  // 🎇 REAL FIREWORK EXPLOSIONS! 🎇
  const fireworkInterval = setInterval(() => {
    if (Date.now() > animationEnd) {
      clearInterval(fireworkInterval);
      return;
    }
    
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Create multiple firework explosions
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        const x = windowWidth * (0.2 + Math.random() * 0.6);
        const y = windowHeight * (0.2 + Math.random() * 0.4);
        const color = fireworkColors[Math.floor(Math.random() * fireworkColors.length)];
        const size = 150 + Math.random() * 100;
        
        // Create the big firework explosion
        createFireworkExplosion(x, y, color, size);
        
        // Add more confetti particles for extra sparkle
        confetti({
          particleCount: 100,
          spread: 360,
          origin: { x: x / windowWidth, y: y / windowHeight },
          colors: [color],
          gravity: 0.6,
          scalar: 1.8,
          drift: 0,
          startVelocity: 50,
          shapes: ['circle', 'square'] // Classic shapes only
        });
      }, i * 200);
    }
  }, 1000);
  
  // 🎊 MORE CONTINUOUS CONFETTI BURSTS! 🎊
  const burstInterval = setInterval(() => {
    if (Date.now() > animationEnd) {
      clearInterval(burstInterval);
      return;
    }
    
    // Multiple random bursts across the screen
    for (let burst = 0; burst < 4; burst++) {
      setTimeout(() => {
        confetti({
          particleCount: 60,
          spread: 100,
          origin: { 
            x: Math.random(),
            y: Math.random() * 0.5 + 0.3
          },
          colors: fireworkColors,
          gravity: 0.7,
          scalar: 1.5,
          drift: Math.random() * 0.4 - 0.2,
          startVelocity: 40,
          shapes: ['circle', 'square'] // Classic shapes only
        });
      }, burst * 100);
    }
  }, 500);
  
  // 🎆💥 EPIC GRAND FINALE! 💥🎆
  setTimeout(() => {
    // MASSIVE finale sequence with multiple waves
    for (let wave = 0; wave < 3; wave++) {
      setTimeout(() => {
        // Multiple simultaneous explosions per wave
        for (let finale = 0; finale < 5; finale++) {
          setTimeout(() => {
            confetti({
              particleCount: 200,
              spread: 160,
              origin: { 
                x: finale * 0.2 + 0.1,
                y: 0.3 + Math.random() * 0.4
              },
              colors: fireworkColors,
              gravity: 0.4,
              scalar: 2.5,
              drift: 0,
              startVelocity: 80,
              shapes: ['circle', 'square'] // Classic shapes only
            });
          }, finale * 50);
        }
      }, wave * 300);
    }
    
    // ULTIMATE FINALE BURST - the biggest one!
    setTimeout(() => {
      for (let ultimate = 0; ultimate < 8; ultimate++) {
        setTimeout(() => {
          confetti({
            particleCount: 300,
            spread: 180,
            origin: { 
              x: ultimate * 0.125 + 0.0625,
              y: 0.5
            },
            colors: fireworkColors,
            gravity: 0.3,
            scalar: 3,
            drift: Math.random() * 0.6 - 0.3,
            startVelocity: 100,
            shapes: ['circle', 'square'] // Classic shapes only
          });
        }, ultimate * 80);
      }
    }, 1000);
  }, duration - 2000);
  
  // 🎵 SMOOTH FADE OUT AT THE END OF CELEBRATION! 🎵
  setTimeout(() => {
    if (celebrationAudio) {
      const fadeOutDuration = 1000; // 1 second fade out
      const fadeOutInterval = 50; // Update every 50ms for smooth fade
      const fadeOutSteps = fadeOutDuration / fadeOutInterval;
      const volumeStep = celebrationAudio.volume / fadeOutSteps;
      
      const fadeOut = setInterval(() => {
        if (celebrationAudio && celebrationAudio.volume > volumeStep) {
          celebrationAudio.volume = Math.max(0, celebrationAudio.volume - volumeStep);
        } else {
          // Fade complete - stop and cleanup
          clearInterval(fadeOut);
          if (celebrationAudio) {
            celebrationAudio.pause();
            celebrationAudio.currentTime = 0; // Reset to beginning for next time
            celebrationAudio = null; // Clean up reference
          }
        }
      }, fadeOutInterval);
    }
  }, duration);
};

/**
 * Main confetti trigger that checks user settings and uses appropriate animation
 */
export const triggerHiredConfetti = async () => {
	const isRavePartyMode = await getRavePartyMode();
	
	if (isRavePartyMode)
		triggerRavePartyConfetti();
	else
		triggerSoftConfetti();
};
